import type { ReactElement } from "react";

import { useSafeAreaInsets } from "react-native-safe-area-context";

import { TotalOnboardingSteps } from "@/constants/Onboarding";
import { OnboardingProgressCard } from "@/ui/components/onboarding/v2/cards/OnboardingProgressCard";
import { getOnboardingHeaderTitle } from "@/ui/components/onboarding/v2/content/OnboardingHeaderContent";
import { NavigationIconButton, Text } from "@/ui/core";
import EquipLogoInline from "@/ui/core/EquipLogoInline";
import { Box, HStack, VStack } from "@/ui/layout";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface OnboardingContentHeaderProps extends ViewWithTestID {
  currentStepIndex: number;
  onClosePress: () => void;
  patientName?: string;
}

const OnboardingContentHeader = ({
  currentStepIndex,
  onClosePress,
  patientName,
  testID,
}: OnboardingContentHeaderProps): ReactElement => {
  const { top } = useSafeAreaInsets();
  const title = getOnboardingHeaderTitle(patientName);

  return (
    <VStack
      paddingHorizontal={LayoutToken.ContentPaddingHorizontal}
      paddingTop={top}
      width="100%"
    >
      <HStack alignItems="center" justifyContent="space-between">
        <EquipLogoInline size="sm" />
        <NavigationIconButton
          accessibilityLabel="Close onboarding"
          onPress={onClosePress}
          testID={joinTestIDs(testID, "close")}
          type="close"
        />
      </HStack>
      <Box paddingTop={14} />
      <Text
        color={ColorToken.NeutralPrimary}
        testID={joinTestIDs(testID, "title")}
        variant="h3"
      >
        {title}
      </Text>
      <Box paddingTop={16}>
        <OnboardingProgressCard
          currentStepIndex={currentStepIndex}
          patientName={patientName}
          testID={testID}
          totalSteps={TotalOnboardingSteps}
        />
      </Box>
    </VStack>
  );
};

export type { OnboardingContentHeaderProps };
export { OnboardingContentHeader };
